import { CardData, CardKind, getCardKind, isExtraDeckCard } from '../types/cards';
import { rollCardFoil } from '../types/foil';
import { MatchState, Seat } from '../types/state';
import { DECK_LIMITS } from './deckValidation';
import {
  EXODIA_PIECE_SET,
  cpuCopyCap,
  expandRecipeMain,
  recipeById,
  recipeHasPolymerization,
  recipeIdForDifficulty,
  type AiRecipe,
  type AiRecipeId,
} from './aiRecipes';
import { seedTestCardsIntoDeck } from './testHandSeeds';

const MAIN_SIZE = 40;
const EASY_EXTRA_CAP = 4;
/** LOB 4-star beaters above this are "good"; Easy filler prefers the rest. */
const EASY_LOW_ATK_MAX = 1200;
/** Test difficulty: small bodies the CPU can Normal Summon, and not walls. */
const TEST_LOW_ATK_MAX = 800;
const TEST_LOW_DEF_MAX = 1000;

/**
 * How stacked the CPU deck is. Test is only low-ATK Normal Monsters.
 * Every other difficulty loads a random published recipe (ten lists each).
 * Hidden cards stay hidden at every level; difficulty only changes how
 * cautiously the planner treats that risk.
 */
export const AI_DECK_DIFFICULTIES = [
  'test',
  'easy',
  'normal',
  'hard',
  'expert',
  'master',
] as const;

export type AiDeckDifficulty = (typeof AI_DECK_DIFFICULTIES)[number];

/**
 * Catalog rows the AI deck builder accepts (LOB JSON or already-mapped CardData).
 */
export interface AiCatalogCard {
  id: string | number;
  name: string;
  type: string;
  level?: number;
  typeline?: string[];
  humanReadableCardType?: string;
  desc?: string;
  atk?: number;
  def?: number;
  race?: string;
  attribute?: string;
  archetype?: string;
  frameType?: string;
}

/**
 * Coerce persisted / query values to a known difficulty. Unknown → Normal.
 */
export function parseAiDeckDifficulty(value: unknown): AiDeckDifficulty {
  if (
    typeof value === 'string' &&
    (AI_DECK_DIFFICULTIES as readonly string[]).includes(value)
  ) {
    return value as AiDeckDifficulty;
  }
  return 'normal';
}

/** Hard, Expert, and Master. */
export function aiIsHighDifficulty(level: AiDeckDifficulty): boolean {
  return level === 'hard' || level === 'expert' || level === 'master';
}

/** Difficulty this CPU seat plans at. Human seats are treated as Normal. */
export function plannerDifficulty(state: MatchState, seat: Seat): AiDeckDifficulty {
  return parseAiDeckDifficulty(state.seats[seat].aiDifficulty);
}

/**
 * Builds a 40-card LOB CPU deck from a published recipe for this difficulty.
 * Pass `recipeId` to force a list (manual probes); otherwise one is picked
 * at random and the previous pick for that difficulty is avoided.
 */
export function buildCuratedAiDeck(
  catalog: AiCatalogCard[],
  difficulty: AiDeckDifficulty = 'normal',
  recipeId?: AiRecipeId,
): { mainDeck: CardData[]; extraDeck: CardData[] } {
  const level = parseAiDeckDifficulty(difficulty);
  const all = catalog.map(toCardData);
  const byId = new Map(all.map((c) => [c.id, c]));
  if (level === 'test') return buildLowAtkTestDeck(all);
  const monsters = all.filter(
    (c) => getCardKind(c) === CardKind.MONSTER && !isExtraDeckCard(c),
  );
  const low = monsters.filter((c) => (c.level ?? 0) <= 4);
  const fusions = all.filter((c) => isExtraDeckCard(c));
  const recipe = recipeById(recipeId ?? recipeIdForDifficulty(level));

  const deck: CardData[] = [];
  buildRecipeMain(deck, byId, recipe, low, level);
  fillToMainSize(deck, low, level, recipe);

  const extraCap = level === 'easy' ? EASY_EXTRA_CAP : DECK_LIMITS.EXTRA_MAX;
  const mainDeck = seedTestCardsIntoDeck(
    shuffle(deck).slice(0, MAIN_SIZE),
    (id) => byId.get(id),
  );
  const mainIds = new Set(mainDeck.map((c) => c.id));
  return {
    mainDeck,
    extraDeck: extraForRecipe(fusions, mainIds, recipe, extraCap, all),
  };
}

/**
 * Forty Normal Monsters the CPU can Normal Summon, ATK and DEF under the
 * test caps. No Effect Monsters, Spells, Traps, Fusions, or Exodia pieces,
 * and no opening-hand pins.
 */
function buildLowAtkTestDeck(
  all: CardData[],
): { mainDeck: CardData[]; extraDeck: CardData[] } {
  const pool = all.filter((card) => {
    if (getCardKind(card) !== CardKind.MONSTER || isExtraDeckCard(card)) return false;
    if (!isNormalMonster(card)) return false;
    if ((card.level ?? 0) > 4) return false;
    if ((card.atk ?? 0) > TEST_LOW_ATK_MAX) return false;
    if ((card.def ?? 0) > TEST_LOW_DEF_MAX) return false;
    if (EXODIA_PIECE_SET.has(card.id)) return false;
    return true;
  });
  const deck: CardData[] = [];
  fillFromPool(deck, shuffle(pool), DECK_LIMITS.MAX_COPIES);
  return { mainDeck: shuffle(deck).slice(0, MAIN_SIZE), extraDeck: [] };
}

function isNormalMonster(card: CardData): boolean {
  if (card.typeline?.some((part) => part.toLowerCase() === 'normal')) return true;
  return card.type.toLowerCase().includes('normal');
}

function buildRecipeMain(
  deck: CardData[],
  byId: Map<string, CardData>,
  recipe: AiRecipe,
  low: CardData[],
  level: Exclude<AiDeckDifficulty, 'test'>,
): void {
  const lines = expandRecipeMain(recipe, level);
  for (const line of lines) {
    const card = byId.get(line.id);
    if (card) pushCopies(deck, card, line.copies);
  }
  fillFromPool(
    deck,
    recipeFiller(low, recipe, level),
    DECK_LIMITS.MAX_COPIES,
    (id) => cpuCopyCap(id, level),
  );
}

function recipeFiller(
  low: CardData[],
  recipe: AiRecipe,
  level: AiDeckDifficulty,
): CardData[] {
  const themed = themeFilter(low, recipe);
  if (
    recipe.id === 'exodia' ||
    recipe.id === 'exobeat' ||
    recipe.id === 'will' ||
    recipe.id === 'goblin'
  ) {
    return [...themed].sort((a, b) => (b.def ?? 0) - (a.def ?? 0));
  }
  if (level === 'easy') {
    const modest = themed.filter((c) => (c.atk ?? 0) <= EASY_LOW_ATK_MAX);
    const pool = modest.length > 0 ? modest : themed;
    return [...pool].sort((a, b) => (a.atk ?? 0) - (b.atk ?? 0));
  }
  return [...themed].sort((a, b) => (b.atk ?? 0) - (a.atk ?? 0));
}

function themeFilter(pool: CardData[], recipe: AiRecipe): CardData[] {
  let out = pool;
  if (recipe.theme?.race) {
    out = out.filter((c) => c.race === recipe.theme?.race);
  }
  if (recipe.theme?.attribute) {
    const attr = recipe.theme.attribute.toUpperCase();
    out = out.filter((c) => (c.attribute ?? '').toUpperCase() === attr);
  }
  return out;
}

function fillFromPool(
  deck: CardData[],
  pool: CardData[],
  copiesPerCard: number,
  capForId?: (id: string) => number,
): void {
  const cap = Math.min(copiesPerCard, DECK_LIMITS.MAX_COPIES);
  for (let pass = 0; pass < cap && deck.length < MAIN_SIZE; pass += 1) {
    for (const m of pool) {
      if (deck.length >= MAIN_SIZE) return;
      const max = capForId ? Math.min(cap, capForId(m.id)) : cap;
      tryPush(deck, m, max);
    }
  }
}

function fillToMainSize(
  deck: CardData[],
  low: CardData[],
  level: AiDeckDifficulty,
  recipe: AiRecipe,
): void {
  const themed = themeFilter(low, recipe);
  const unused = themed.filter((c) => !deck.some((d) => d.id === c.id));
  const modest = unused.filter((c) => (c.atk ?? 0) <= EASY_LOW_ATK_MAX);
  const preferred = level === 'easy' && modest.length > 0 ? modest : unused;
  const maxOf = (id: string) =>
    level === 'test' ? DECK_LIMITS.MAX_COPIES : cpuCopyCap(id, level);
  const filler = shuffle(preferred);
  for (const m of filler) {
    if (deck.length >= MAIN_SIZE) return;
    tryPush(deck, m, maxOf(m.id));
  }
  let guard = 0;
  while (deck.length < MAIN_SIZE && themed.length > 0 && guard < 200) {
    const m = themed[guard % themed.length];
    guard += 1;
    tryPush(deck, m, maxOf(m.id));
  }
}

function extraForRecipe(
  fusions: CardData[],
  mainIds: Set<string>,
  recipe: AiRecipe,
  cap: number,
  catalog: CardData[],
): CardData[] {
  if (!recipeHasPolymerization(recipe)) return [];
  const byName = new Map(catalog.map((c) => [c.name, c]));
  const legal = fusions.filter((fusion) =>
    fusionMaterialsInMain(fusion, mainIds, byName),
  );
  return uniqueExtra(legal, cap);
}

function fusionMaterialsInMain(
  fusion: CardData,
  mainIds: Set<string>,
  byName: Map<string, CardData>,
): boolean {
  const names =
    fusion.desc?.match(/"([^"]+)"/g)?.map((m) => m.slice(1, -1)) ?? [];
  if (names.length < 2) return false;
  return names.every((name) => {
    const mat = byName.get(name);
    return !!mat && mainIds.has(mat.id);
  });
}

function uniqueExtra(fusions: CardData[], cap: number): CardData[] {
  const ranked = [...fusions].sort((a, b) => (b.atk ?? 0) - (a.atk ?? 0));
  const seen = new Set<string>();
  const out: CardData[] = [];
  for (const f of ranked) {
    if (seen.has(f.id)) continue;
    seen.add(f.id);
    out.push(cloneCard(f));
    if (out.length >= cap) break;
  }
  return out;
}

function toCardData(raw: AiCatalogCard): CardData {
  const card: CardData = {
    id: String(raw.id),
    name: raw.name,
    type: raw.type,
  };
  if (raw.level !== undefined) card.level = raw.level;
  if (raw.typeline !== undefined) card.typeline = raw.typeline;
  if (raw.humanReadableCardType !== undefined) {
    card.humanReadableCardType = raw.humanReadableCardType;
  }
  if (raw.desc !== undefined) card.desc = raw.desc;
  if (raw.atk !== undefined) card.atk = raw.atk;
  if (raw.def !== undefined) card.def = raw.def;
  if (raw.race !== undefined) card.race = raw.race;
  if (raw.attribute !== undefined) card.attribute = raw.attribute;
  if (raw.archetype !== undefined) card.archetype = raw.archetype;
  return card;
}

function cloneCard(card: CardData): CardData {
  return { ...card, foil: rollCardFoil() };
}

function copiesOf(deck: CardData[], id: string): number {
  return deck.filter((c) => c.id === id).length;
}

function tryPush(
  deck: CardData[],
  card: CardData,
  maxCopies: number = DECK_LIMITS.MAX_COPIES,
): boolean {
  if (deck.length >= MAIN_SIZE) return false;
  if (copiesOf(deck, card.id) >= maxCopies) return false;
  deck.push(cloneCard(card));
  return true;
}

function pushCopies(deck: CardData[], card: CardData, n: number): void {
  for (let i = 0; i < n; i += 1) tryPush(deck, card);
}

function shuffle<T>(items: T[]): T[] {
  const out = [...items];
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}
